import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AddModal } from '../../../components/AddModal';
import { useScope } from '../../../context/ScopeContext';
import { api, json } from '../../../lib/api';

const yesterday = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

const onlyDigits = (value: string) => value.replace(/\D/g, '');
const wowNamePattern = '[A-Za-z\\s]+';

export function CustomerCreateModal({
  buttonLabel = '+ Add customer',
  onCreated,
  buttonClassName,
}: {
  buttonLabel?: string;
  onCreated?: (customer: any) => void;
  buttonClassName?: string;
}) {
  const scope = useScope();
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [secondPhone, setSecondPhone] = useState('');
  const [email, setEmail] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [address, setAddress] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [campaignCode, setCampaignCode] = useState('');
  const [notes, setNotes] = useState('');

  const sources = useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => api<any[]>('/crm/lead-sources'),
    enabled: Boolean(scope.companyId),
  });

  const reset = () => {
    setFirstName(''); setMiddleName(''); setLastName(''); setPhone(''); setSecondPhone('');
    setEmail(''); setNationalId(''); setAddress(''); setBirthDate(''); setGender(''); setSourceId(''); setCampaignCode(''); setNotes('');
  };

  const submit = async () => {
    if (!scope.companyId || !scope.branchId) throw new Error('Select a company and branch before creating a customer.');
    const customer = await api<any>('/crm/customers', {
      method: 'POST',
      ...json({
        companyId: scope.companyId,
        homeBranchId: scope.branchId,
        firstName,
        middleName,
        lastName,
        phone,
        secondPhone: secondPhone || null,
        email: email || null,
        nationalId: nationalId || null,
        address: address || null,
        birthDate,
        gender,
        sourceId: sourceId || null,
        campaignCode: campaignCode || null,
        active: true,
      }),
    });
    if (notes.trim()) {
      await api(`/crm/customers/${customer.id}/notes`, {
        method: 'POST',
        ...json({ companyId: scope.companyId, body: notes.trim(), visibility: 'INTERNAL' }),
      });
    }
    await queryClient.invalidateQueries({ queryKey: ['customers'] });
    await queryClient.invalidateQueries({ queryKey: ['operation-customers'] });
    reset();
    onCreated?.(customer);
  };

  return <AddModal
    title="Add customer"
    buttonLabel={buttonLabel}
    submitLabel="Create customer"
    subtitle={`WOW ME rules • ${scope.selectedBranch?.name ?? 'Selected branch'}`}
    disabled={!scope.companyId || !scope.branchId}
    onSubmit={submit}
    buttonClassName={buttonClassName}
    width="wide"
  >
    <div className="modal-section-title">Required customer data — WOW ME rules</div>
    <label>First name<input value={firstName} onChange={event => setFirstName(event.target.value)} required pattern={wowNamePattern} title="English letters and spaces only" autoFocus /></label>
    <label>Middle name<input value={middleName} onChange={event => setMiddleName(event.target.value)} required pattern={wowNamePattern} title="English letters and spaces only" /></label>
    <label>Last name<input value={lastName} onChange={event => setLastName(event.target.value)} required pattern={wowNamePattern} title="English letters and spaces only" /></label>
    <label>Birth date<input type="date" value={birthDate} max={yesterday()} onChange={event => setBirthDate(event.target.value)} required /></label>
    <label>Phone<input value={phone} onChange={event => setPhone(onlyDigits(event.target.value).slice(0, 11))} required inputMode="numeric" minLength={11} maxLength={11} pattern="01[0-9]{9}" title="11 digits and must start with 01" placeholder="01xxxxxxxxx" /></label>
    <label>Gender<select value={gender} onChange={event => setGender(event.target.value)} required><option value="">Select gender…</option><option value="MALE">Male</option><option value="FEMALE">Female</option></select></label>

    <div className="modal-section-title">Additional information</div>
    <label>Second phone<input value={secondPhone} onChange={event => setSecondPhone(onlyDigits(event.target.value).slice(0, 32))} inputMode="numeric" /></label>
    <label>Email<input type="email" value={email} onChange={event => setEmail(event.target.value)} /></label>
    <label>National ID<input value={nationalId} onChange={event => setNationalId(event.target.value)} /></label>
    <label>Source<select value={sourceId} onChange={event => setSourceId(event.target.value)}><option value="">No source</option>{(sources.data ?? []).filter(source => source.companyId === scope.companyId).map(source => <option key={source.id} value={source.id}>{source.name}</option>)}</select></label>
    <label>Campaign code<input value={campaignCode} onChange={event => setCampaignCode(event.target.value)} /></label>
    <label className="span-two">Address<textarea value={address} onChange={event => setAddress(event.target.value)} rows={2} /></label>
    <label className="span-two">Notes<textarea value={notes} onChange={event => setNotes(event.target.value)} rows={2} /></label>
  </AddModal>;
}
